/**
 * CoPilot Brain Companion — headless conversation state machine (SR2 P1.1,
 * TECH §19/§30). The `@tonnie/copilot/client` layer every frontend shares:
 * journey-web, business-web, and citizen-mobile render natively over this ONE
 * state machine, so streaming assembly, proposal lifecycle, suggestions,
 * quota, and degraded states behave identically everywhere.
 *
 * Platform-agnostic on purpose — no DOM, no fetch, no React. The transport
 * (an async-iterable of `CoPilotStreamEvent`, produced by the SDK on web or
 * `expo/fetch` on native via `readCoPilotStream`) is INJECTED, so this file
 * has zero I/O and unit-tests as pure logic.
 */

import type {
  CoPilotActionProposal,
  CoPilotResponseEnvelope,
  CoPilotStreamErrorCode,
  CoPilotStreamEvent,
  CoPilotSuggestionCard,
  SafetyBannerConfig,
} from '@tonnie/types';

import { buildSafetyBanner } from '../utils/safety-banner-builder';

export type CoPilotMessageRole = 'user' | 'assistant';

/** A rendered message. Assistant messages accrete tokens + envelopes live. */
export interface CoPilotMessage {
  id: string;
  role: CoPilotMessageRole;
  /** Streamed prose (token deltas concatenated; `final` reconciles it). */
  text: string;
  /** Structured cards emitted for this message (PRD §14). */
  envelopes: CoPilotResponseEnvelope[];
  /** True while tokens are still arriving. */
  streaming: boolean;
  /** Feedback the user gave on this message (null = none yet). */
  feedback: 'up' | 'down' | null;
}

export type CoPilotConnectionState = 'idle' | 'streaming' | 'degraded' | 'error';

/** Live tool progress line ("checking your tasks…") — one at a time. */
export interface CoPilotToolActivity {
  labelKey: string;
}

export interface CoPilotConversationState {
  messages: CoPilotMessage[];
  connection: CoPilotConnectionState;
  /** Pending Tier-1/2 proposals awaiting the user's confirm/dismiss. */
  proposals: CoPilotActionProposal[];
  /** Corner-cue suggestions surfaced this session. */
  suggestions: CoPilotSuggestionCard[];
  toolActivity: CoPilotToolActivity | null;
  /** Set from a typed `error` event — i18n key for the FE to render. */
  errorKey: string | null;
  /**
   * The typed code of that failure — what a client branches on (never the
   * key): `provider_unavailable` is the AI service not answering, which the
   * panels confirm against the brain's health and say calmly.
   */
  errorCode: CoPilotStreamErrorCode | null;
  /** Correlation id of the failed turn — safe to show ("ref: …") + grep in logs. */
  errorTraceId: string | null;
  /** DEV-only raw cause from the backend (omitted in prod) — for the console. */
  errorDetail: string | null;
  /** Last turn ran in degradation (provider failover / budget). */
  degraded: boolean;
  /**
   * The persisted conversation currently open (0089 conversation history).
   * `null` for a brand-new chat that hasn't sent its first message yet — the
   * server lazily creates the row on the first turn and returns the id on the
   * `end` frame, which this store adopts. Sent back as `context.conversationId`
   * on every subsequent turn so the thread stays continuous, and set directly
   * when the user picks a past conversation from history.
   */
  currentConversationId: string | null;
  /**
   * The safety banner of the latest turn, as the server built it (the `end`
   * frame's; a past conversation's from its last answer) — a critical one
   * carries the crisis lines to offer. Null before any turn: the conversation
   * shows the standard banner (`conversationSafetyBanner`).
   */
  safetyBanner: SafetyBannerConfig | null;
}

/**
 * The error keys the CLIENT writes into `errorKey` itself — a turn whose stream
 * threw, an upload that failed. Every other `errorKey` arrives from platform-api,
 * whose `EMITTED_I18N_KEYS` catalog the key-usage gate checks; these never cross
 * the wire, so only packages/locale/src/dynamic-key-coverage.spec.ts proves they
 * exist. Raise a client-side error with one of these, never an inline string.
 */
export const COPILOT_CLIENT_ERROR_KEY = {
  internal: 'copilot.errors.internal',
  uploadFailed: 'copilot.errors.uploadFailed',
} as const;

export function initialConversationState(
  currentConversationId: string | null = null,
  safetyBanner: SafetyBannerConfig | null = null
): CoPilotConversationState {
  return {
    messages: [],
    connection: 'idle',
    proposals: [],
    suggestions: [],
    toolActivity: null,
    errorKey: null,
    errorCode: null,
    errorTraceId: null,
    errorDetail: null,
    degraded: false,
    currentConversationId,
    safetyBanner,
  };
}

/** The banner the conversation shows: its latest turn's, or the standard one. */
export function conversationSafetyBanner(state: CoPilotConversationState): SafetyBannerConfig {
  return state.safetyBanner ?? buildSafetyBanner({ mode: 'brain' });
}

/**
 * Apply one protocol event to the conversation state, returning the next
 * state (immutable — safe for React/observable consumers). Unknown event
 * types are a no-op (forward-compatible with a higher protocol version).
 */
export function reduceConversation(
  state: CoPilotConversationState,
  event: CoPilotStreamEvent
): CoPilotConversationState {
  switch (event.type) {
    case 'token':
      return patchMessage(state, event.messageId, (m) => ({
        ...m,
        text: m.text + event.delta,
        streaming: true,
      }));

    case 'envelope':
      return patchMessage(state, event.messageId, (m) => ({
        ...m,
        envelopes: [...m.envelopes, event.envelope],
      }));

    case 'final':
      // Output-guard rewrite — authoritative reconcile of the prose.
      return patchMessage(state, event.messageId, (m) => ({ ...m, text: event.content }));

    case 'choices': {
      // Follow-up chips delivered AFTER `end` (BL-217a). Append the
      // `tonnie-choices` fence to the already-settled message so each renderer's
      // `parseChoices` turns it into tappable chips — WITHOUT going back through
      // `patchMessage` (which would re-set `connection: 'streaming'`). The turn
      // is done; the cursor already stopped on `end`, and this only adds chips.
      if (!event.options.length) return state;
      const fence = `\n\n\`\`\`tonnie-choices\n${event.options.join('\n')}\n\`\`\``;
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === event.messageId && !m.text.includes('```tonnie-choices')
            ? { ...m, text: `${m.text}${fence}` }
            : m
        ),
      };
    }

    case 'tool_activity':
      return {
        ...state,
        toolActivity: event.status === 'started' ? { labelKey: event.toolLabelKey } : null,
      };

    case 'tool_proposal':
      return { ...state, proposals: [...state.proposals, event.proposal] };

    case 'tool_result':
      // A concluded proposal drops out of the pending list; its outcome card
      // (if any) rides the associated message via a separate envelope event.
      return {
        ...state,
        proposals: event.proposalId
          ? state.proposals.filter((p) => p.proposalId !== event.proposalId)
          : state.proposals,
        toolActivity: null,
      };

    case 'suggestion':
      return { ...state, suggestions: mergeSuggestions(state.suggestions, event.suggestions) };

    case 'error':
      // Always surface the failure to the browser console with the correlation
      // id (+ dev-only cause) so a silent backend failure is ALWAYS traceable.
      if (typeof console !== 'undefined') {
        console.error(
          `[copilot] turn failed — ${event.code} (ref: ${event.traceId ?? 'n/a'})`,
          event.detail ?? ''
        );
      }
      return {
        ...state,
        connection: 'error',
        errorKey: event.messageKey,
        errorCode: event.code,
        errorTraceId: event.traceId ?? null,
        errorDetail: event.detail ?? null,
      };

    case 'end':
      return {
        ...state,
        connection: event.degraded ? 'degraded' : 'idle',
        degraded: !!event.degraded,
        toolActivity: null,
        // Adopt the conversation id the server resolved/created for this turn
        // (0089). A brand-new chat (currentConversationId === null) learns its
        // id here after its first message; an existing thread just re-confirms
        // it. Never clobber a known id with an absent one (error/empty turns
        // omit it).
        currentConversationId: event.conversationId ?? state.currentConversationId,
        // The turn's banner, as the server built it — a turn refused before the
        // brain sends none and leaves the last one standing.
        safetyBanner: event.safetyBanner ?? state.safetyBanner,
        messages: state.messages.map((m) =>
          m.id === event.messageId ? { ...m, streaming: false } : m
        ),
      };

    default:
      // token/envelope/final/tool_*/suggestion/error/end handled above;
      // tts_chunk is consumed by the voice layer, not the text state.
      return state;
  }
}

// ── helpers ──────────────────────────────────────────────────────────────

function patchMessage(
  state: CoPilotConversationState,
  messageId: string,
  patch: (m: CoPilotMessage) => CoPilotMessage
): CoPilotConversationState {
  const exists = state.messages.some((m) => m.id === messageId);
  const messages = exists
    ? state.messages.map((m) => (m.id === messageId ? patch(m) : m))
    : [...state.messages, patch(emptyAssistant(messageId))];
  return { ...state, connection: 'streaming', errorKey: null, errorCode: null, messages };
}

function emptyAssistant(id: string): CoPilotMessage {
  return { id, role: 'assistant', text: '', envelopes: [], streaming: true, feedback: null };
}

/** Dedupe suggestions by id, newest wins, cap at the PRD §12 limit of 2. */
function mergeSuggestions(
  current: CoPilotSuggestionCard[],
  incoming: CoPilotSuggestionCard[]
): CoPilotSuggestionCard[] {
  const byId = new Map(current.map((s) => [s.suggestionId, s]));
  for (const s of incoming) byId.set(s.suggestionId, s);
  return [...byId.values()].slice(-2);
}

/** Append a user message (local echo) before opening the stream. */
export function appendUserMessage(
  state: CoPilotConversationState,
  id: string,
  text: string
): CoPilotConversationState {
  const message: CoPilotMessage = {
    id,
    role: 'user',
    text,
    envelopes: [],
    streaming: false,
    feedback: null,
  };
  return { ...state, messages: [...state.messages, message], errorKey: null, errorCode: null };
}

/** Record local feedback on a message (dispatch to the API is the caller's job). */
export function setMessageFeedback(
  state: CoPilotConversationState,
  messageId: string,
  feedback: 'up' | 'down'
): CoPilotConversationState {
  return {
    ...state,
    messages: state.messages.map((m) => (m.id === messageId ? { ...m, feedback } : m)),
  };
}

/**
 * Load a chosen past conversation into the state (0089 history "continue").
 * Replaces the message list with the fetched thread and pins its id so the next
 * turn addresses it. Resets transient error/proposal/tool state from the
 * conversation being left. `id === null` + empty `messages` is the "new chat"
 * reset (client-only, no server row until the first turn). `safetyBanner` is
 * the banner of the thread's last answer (null for a new chat).
 */
export function loadConversation(
  id: string | null,
  messages: CoPilotMessage[],
  safetyBanner: SafetyBannerConfig | null = null
): CoPilotConversationState {
  return {
    ...initialConversationState(id, safetyBanner),
    messages,
  };
}

/** Remove a proposal the user dismissed (no execution). */
export function dismissProposal(
  state: CoPilotConversationState,
  proposalId: string
): CoPilotConversationState {
  return { ...state, proposals: state.proposals.filter((p) => p.proposalId !== proposalId) };
}

/**
 * Append a non-streamed assistant notice (e.g. the "✓ done" confirmation shown
 * after the user taps Confirm on a proposal card). Renders as a normal assistant
 * bubble but is NOT eligible for feedback (id `notice-*` is excluded upstream).
 */
export function appendAssistantNotice(
  state: CoPilotConversationState,
  id: string,
  text: string
): CoPilotConversationState {
  const message: CoPilotMessage = {
    id,
    role: 'assistant',
    text,
    envelopes: [],
    streaming: false,
    feedback: null,
  };
  return { ...state, messages: [...state.messages, message] };
}
